import type { Query, QueryClient } from '@tanstack/react-query';

/** Same-origin channel shared by every window of the app. */
export const ALERT_SYNC_CHANNEL = 'kirin-alert-sync';

const ORG_ALERT_KINDS = new Set([
  'groupedAlerts',
  'alertOccurrences',
  'alertRef',
]);

/**
 * Queries that render alert state: the alert lists and single-alert reads
 * (org and personal), notification landings, and session timelines (Keep).
 */
export function isAlertQuery(query: Pick<Query, 'queryKey'>): boolean {
  const [head, kind] = query.queryKey;
  if (head === 'alerts' || head === 'sessionTimeline') return true;
  if (head === 'organizations') return ORG_ALERT_KINDS.has(kind as string);
  return head === 'account' && kind === 'alertRef';
}

/**
 * Keeps the app's windows in step — the alerts table and an alert's pop-out
 * window each have their own QueryClient, so a change made in one would stay
 * invisible in the other. Every successful change in this window is announced,
 * and an announcement from another window refetches this window's alert data
 * (only what is on screen refetches; the rest is marked stale).
 *
 * @returns Stops listening and closes the channel.
 */
export function syncAlertsAcrossWindows(queryClient: QueryClient): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const channel = new BroadcastChannel(ALERT_SYNC_CHANNEL);
  channel.onmessage = () => {
    void queryClient.invalidateQueries({ predicate: isAlertQuery });
  };
  // Announce mutations, not invalidations: a refetch here never announces, so
  // windows cannot echo each other.
  const unsubscribe = queryClient.getMutationCache().subscribe(event => {
    if (event.type === 'updated' && event.action.type === 'success') {
      channel.postMessage('changed');
    }
  });
  return () => {
    unsubscribe();
    channel.close();
  };
}
